/**
 * The API session: an opaque bearer token valid for 8 hours, without refresh. Stored in localStorage so every
 * tab shares it; a BroadcastChannel tells the other tabs about logins and logouts.
 */
export interface SessionUser {
  id: string;
  version: number;
  email: string;
  name: string;
  active: boolean;
  roleIds: string[];
}

export interface Session {
  token: string;
  expiresAt: string;
  user: SessionUser;
}

const KEY = 'dec.session';
type Listener = (session: Session | null) => void;
const listeners = new Set<Listener>();
let current: Session | null = read();

const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('dec-session');
channel?.addEventListener('message', () => {
  current = read();
  listeners.forEach((l) => l(current));
});

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (!s.token || new Date(s.expiresAt).getTime() <= Date.now()) return null;
    return s;
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  if (current && new Date(current.expiresAt).getTime() <= Date.now()) current = null;
  return current;
}

export function setSession(session: Session | null): void {
  current = session;
  try {
    if (session) localStorage.setItem(KEY, JSON.stringify(session));
    else localStorage.removeItem(KEY);
  } catch {
    // Private mode without storage: the session lives only in memory for this tab.
  }
  channel?.postMessage(session ? 'login' : 'logout');
  listeners.forEach((l) => l(session));
}

export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
